import React from 'react';
import AIBadge from '@/components/shared/AIBadge';
import Icon from '@/components/ui/Icon';
import { COLORS } from '@/components/ui/ui.constants';
import cn from '@/utils/cn';
import useAIQuestionBuilderStore
  from '@/components/staffer/ExerciseBuilder/AIQuestionBuilder/useAIQuestionBuilderStore';
import type { AIGeneratedQuestion, AIQuestionChoice } from './types';

interface QuestionCardProps {
  dimmed: boolean;
  index: number;
  question: AIGeneratedQuestion;
  selected: boolean;
  onToggle: () => void;
}

const ChoiceIndicator = ({ correct }: { correct: boolean }) => (
  <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-shrink-0">
    {correct
      ? <Icon fill={COLORS.green} icon="Check" size={14} />
      : <span className="tw-text-dark-grey tw-text-sm">-</span>}
  </span>
);

const ChoiceItem = ({ choice }: { choice: AIQuestionChoice }) => (
  <li className="tw-flex tw-items-start tw-gap-2">
    <ChoiceIndicator correct={choice.correct} />
    <span className={cn('tw-font-tci tw-text-sm tw-leading-snug')}>
      {choice.label}. {choice.text}
    </span>
  </li>
);

const ChoiceList = ({ choices }: { choices: AIQuestionChoice[] }) => (
  <>
    {choices.map(choice => (
      <ChoiceItem key={choice.label} choice={choice} />
    ))}
  </>
);

const ComparisonColumn = ({
  choices,
  className,
  label,
}: {
  choices: AIQuestionChoice[];
  className?: string;
  label: string;
}) => (
  <div className={className}>
    <strong>{label}</strong>
    <ChoiceList choices={choices} />
  </div>
);

const QuestionCard = ({
  dimmed, index, question, selected, onToggle,
}: QuestionCardProps) => {
  const { answerCount, creationMode } = useAIQuestionBuilderStore();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <div
      className={cn(
        'tw-rounded tw-bg-white tw-overflow-hidden tw-cursor-pointer',
        'tw-transition-all tw-mb-3 tw-relative',
        'tw-border tw-border-solid',
        selected
          ? 'tw-border-green hover:tw-border-[#15803d]'
          : 'tw-border-grey hover:tw-border-gray-400 hover:tw-bg-gray-50',
        dimmed && 'tw-opacity-50',
      )}
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={handleKeyDown}
    >
      {selected && (
        <div className="tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-w-[7px] tw-bg-green" />
      )}
      <div
        className={cn(
          'tw-flex tw-items-center tw-justify-between',
          'tw-px-5 tw-py-1.5 tw-border-b tw-border-solid tw-border-grey',
        )}
      >
        <div className="tw-flex tw-items-center tw-gap-2">
          <span
            className={cn(
              'tw-font-tci tw-text-xs tw-font-bold tw-text-dark-grey',
            )}
          >
            Q{index + 1}
          </span>
          <div className="tw-w-px tw-h-6 tw-bg-grey" />
          <AIBadge />
          <span
            className="tw-font-tci tw-text-xs tw-text-dark-grey"
          >
            {creationMode === 'create' ? question.source_sections?.[0]?.title
              : `${question.original?.choices?.length ?? '?'} to ${answerCount} choices`}
          </span>
        </div>
        <input
          aria-label={`Select question ${index + 1}`}
          checked={selected}
          className="tw-w-3.5 tw-h-3.5 tw-cursor-pointer tw-accent-[#1b63dc]"
          type="checkbox"
          onChange={onToggle}
          onClick={e => e.stopPropagation()}
        />
      </div>
      <div className="tw-px-5 tw-pt-2 tw-pb-4">
        <p
          className={cn(
            'tw-font-tci tw-text-sm tw-font-semibold',
            'tw-text-tci-black tw-leading-snug',
            'tw-mb-3',
          )}
        >
          {question.stem}
        </p>
        <ul className="tw-list-none tw-p-0 tw-m-0 tw-pl-4 tw-space-y-1">
          {creationMode === 'create' ? (
            <div className="tw-text-tci-black">
              <ChoiceList choices={question.choices} />
            </div>
          ) : (
            <div className="tw-flex tw-justify-between tw-w-full">
              <ComparisonColumn
                choices={question.original.choices}
                className="tw-flex-1 tw-text-gray-400"
                label="BEFORE"
              />
              <ComparisonColumn
                choices={question.choices}
                className="tw-flex-1 tw-border-l tw-border-solid tw-pl-10 tw-border-grey tw-text-tci-black"
                label="AFTER"
              />
            </div>
          )}
        </ul>
      </div>
    </div>
  );
};

export default QuestionCard;
